import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useSystem } from '../../../hooks/useSystem';
import { observer } from 'mobx-react';
import { ProfileModel } from '../../../core/models/ProfileModel';
import { GetProfilesRequest } from '@foxbun/lib-goferal-types/dist';
import * as _ from 'lodash';
import { SearchInputWidget } from '../../../widgets/forms/SearchInputWidget';
import { EmptyWidget } from '../../../widgets/content/EmptyWidget';
import { AnimatePageWidget } from '../../../widgets/layout/AnimatePageWidget';
import { MediaIconWidget } from '../../../widgets/content/MediaIconWidget';
import { AppLink } from '../../../widgets/utils';
import { IconGridLayoutWidget } from '../../../widgets/collection/IconGridLayoutWidget';
import { useBufferedSearch } from '../../../hooks/useBufferedSearch';

export const ProfilesPage: React.FC = observer((props) => {
  const system = useSystem();

  const normalizeReq = (req) => {
    let res = _.pickBy(req, (value) => value != null);
    if (Object.keys(res).length === 0) {
      res = {
        location: {
          distance_meters: 1000
        }
      } as GetProfilesRequest;
    }
    return res;
  };

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState<string>(null);
  const [req, setReq] = useState<GetProfilesRequest>(normalizeReq({}));
  const [users, setUsers] = useState<ProfileModel[]>([]);

  const loadUsers = useCallback(() => {
    setLoading(true);
    system.user_store
      .getProfiles(req)
      .then((users) => {
        setUsers(users);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [JSON.stringify(req)]);

  // reload users when position is recorded
  useEffect(() => {
    return system.session.geoLocationEngine.registerListener({
      recorded: () => {
        if (req.location) {
          loadUsers();
        }
      }
    });
  }, [JSON.stringify(req)]);

  // reload users when filter criteria changes
  useEffect(() => {
    loadUsers();
  }, [JSON.stringify(req)]);

  useBufferedSearch({
    search,
    cb: (search) => {
      setReq(
        normalizeReq({
          profile_name: search
        })
      );
    }
  });

  return (
    <AnimatePageWidget slide={false}>
      <S.Users>
        <S.Controls>
          <S.Input
            value={search}
            placeholder="search profile names"
            loading={loading}
            onChange={(search) => {
              setSearch(search);
            }}
          />
        </S.Controls>
        {!loading && users.length === 0 ? <EmptyWidget label="No profiles found" /> : null}
        {users.length > 0 ? (
          <IconGridLayoutWidget<ProfileModel>
            gap={10}
            cols={3}
            elements={users}
            generateElement={(u) => {
              return (
                <S.LinkWrapper key={u.key} to={u.link} push={true}>
                  <S.Media
                    font_size={40}
                    media={{
                      name: u.data.profile_name,
                      image: u.image
                    }}
                  />
                </S.LinkWrapper>
              );
            }}
          />
        ) : null}
      </S.Users>
    </AnimatePageWidget>
  );
});

namespace S {
  export const Input = styled(SearchInputWidget)``;

  export const Controls = styled.div`
    display: flex;
    flex-direction: column;
  `;

  export const Users = styled.div`
    padding: 10px;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  `;

  export const Media = styled(MediaIconWidget)`
    border-radius: 4px;
  `;

  export const LinkWrapper = styled(AppLink)`
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    width: 100%;
    height: 100%;
  `;
}
